import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { organizationSettingsAPI } from '../../services/api';
import { apiErrorMessage } from '../../utils/apiError';
import Modal from '../Common/admin/Modal';

const FORM_ID = 'ad-office-settings-form';

/**
 * The office's commercial terms — currently its commission and VAT, which the
 * financial reports use to work out what the office earns.
 *
 * Opened from the sidebar, the user menu and the landing page, so it carries
 * its own loading and saving; the caller only owns `show`.
 */
const OfficeSettingsModal = ({ show, onClose }) => {
  const { t } = useTranslation();
  const { isSuperAdmin, isOrgAdmin } = useAuth();
  const canEdit = isSuperAdmin || isOrgAdmin;

  const [form, setForm] = useState({
    commissionPercentage: '',
    commissionIsPercentage: true,
    commissionFixedAmount: '',
    vatPercentage: '',
    notes: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState(null);
  const [done, setDone] = useState(false);

  // Reload on every open: another admin may have changed the terms since.
  useEffect(() => {
    if (!show) return;
    setLoading(true);
    setFailure(null);
    setDone(false);

    organizationSettingsAPI
      .get()
      .then(({ data }) =>
        setForm({
          commissionPercentage: data.commissionPercentage ?? 0,
          commissionIsPercentage: data.commissionIsPercentage !== false,
          commissionFixedAmount: data.commissionFixedAmount ?? 0,
          vatPercentage: data.vatPercentage ?? 0,
          notes: data.notes || '',
        })
      )
      .catch((err) => setFailure(apiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [show]);

  const set = (name) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [name]: value }));
    setDone(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFailure(null);
    setDone(false);
    try {
      await organizationSettingsAPI.update({
        commissionPercentage: Number(form.commissionPercentage) || 0,
        commissionIsPercentage: form.commissionIsPercentage,
        commissionFixedAmount: Number(form.commissionFixedAmount) || 0,
        vatPercentage: Number(form.vatPercentage) || 0,
        notes: form.notes || null,
      });
      setDone(true);
    } catch (err) {
      setFailure(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const percentage = form.commissionIsPercentage;

  return (
    <Modal
      show={show}
      title={t('settings.title')}
      subtitle={t('settings.subtitle')}
      onClose={onClose}
      footer={
        <>
          {canEdit && (
            <button
              type="submit"
              form={FORM_ID}
              className="ad-btn ad-btn-primary"
              disabled={saving || loading}
            >
              {saving ? t('common.loading') : t('common.save')}
            </button>
          )}
          <button type="button" className="ad-btn" onClick={onClose}>
            {t('common.close')}
          </button>
        </>
      }
    >
      {failure && <p className="ad-error-text">{failure}</p>}
      {done && <p className="ad-profile-done">{t('settings.saved')}</p>}
      {loading && <p className="ad-column-note">{t('tree.loading')}</p>}

      {!loading && (
        <form id={FORM_ID} onSubmit={submit}>
          <div className="ad-modal-grid">
            <div className="ad-modal-field ad-modal-field-full">
              <label className="ad-modal-label">{t('settings.commissionType')}</label>
              <div className="ad-freq-options">
                <label className={`ad-freq-option ${percentage ? 'ad-freq-option-active' : ''}`}>
                  <input
                    type="radio"
                    checked={percentage}
                    onChange={() => setForm((prev) => ({ ...prev, commissionIsPercentage: true }))}
                    disabled={!canEdit}
                  />
                  {t('settings.percentage')}
                </label>
                <label className={`ad-freq-option ${!percentage ? 'ad-freq-option-active' : ''}`}>
                  <input
                    type="radio"
                    checked={!percentage}
                    onChange={() => setForm((prev) => ({ ...prev, commissionIsPercentage: false }))}
                    disabled={!canEdit}
                  />
                  {t('settings.fixed')}
                </label>
              </div>
            </div>

            {percentage ? (
              <div className="ad-modal-field">
                <label className="ad-modal-label">{t('settings.commissionPercentage')}</label>
                <span className="ad-input-wrap">
                  <i className="fas fa-percent ad-input-icon"></i>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    className="ad-input-sm ad-input-has-icon"
                    value={form.commissionPercentage}
                    onChange={set('commissionPercentage')}
                    disabled={!canEdit}
                  />
                </span>
              </div>
            ) : (
              <div className="ad-modal-field">
                <label className="ad-modal-label">{t('settings.commissionFixed')}</label>
                <span className="ad-input-wrap">
                  <i className="fas fa-sack-dollar ad-input-icon"></i>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="ad-input-sm ad-input-has-icon"
                    value={form.commissionFixedAmount}
                    onChange={set('commissionFixedAmount')}
                    disabled={!canEdit}
                  />
                </span>
              </div>
            )}

            <div className="ad-modal-field">
              <label className="ad-modal-label">{t('settings.vat')}</label>
              <span className="ad-input-wrap">
                <i className="fas fa-receipt ad-input-icon"></i>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  className="ad-input-sm ad-input-has-icon"
                  value={form.vatPercentage}
                  onChange={set('vatPercentage')}
                  disabled={!canEdit}
                />
              </span>
            </div>

            <div className="ad-modal-field ad-modal-field-full">
              <label className="ad-modal-label">{t('settings.notes')}</label>
              <textarea
                className="ad-textarea"
                value={form.notes}
                onChange={set('notes')}
                disabled={!canEdit}
              />
            </div>
          </div>

          <p className="ad-hint">{t('settings.commissionHint')}</p>
        </form>
      )}
    </Modal>
  );
};

export default OfficeSettingsModal;
